/**
 * Created at 2023/08/14
 *
 * NOTE: this file has lower level than `styleUtils.ts`
 * @2026/01/21
 */

import * as ol from 'ol';
import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { getCenter, getWidth, getHeight, boundingExtent } from 'ol/extent';
import {
  Circle,
  Point,
  Polygon,
  Geometry,
  SimpleGeometry,
  LineString,
  MultiLineString,
} from 'ol/geom';

// ============= geter & setter section =======================

/**
 * @deprecated in favor of isCircleGeometry
 * @param geometry
 * @returns
 */
export const isCircle = (geometry: Geometry | undefined) => {
  return geometry instanceof Circle;
};

export const isCircleGeometry = (geometry: Geometry | undefined) => {
  if (!geometry) return false;
  return geometry.getType() === 'Circle';
};

export const isPolygon = (geometry: Geometry | undefined) => {
  return geometry instanceof Polygon;
};

export const isPolygonGeometry = (geometry: Geometry | undefined) => {
  if (!geometry) return false;
  return geometry.getType() === 'Polygon';
};

export const isLineString = (geometry: Geometry | undefined) => {
  return geometry instanceof LineString;
};

export const isMultiLineString = (geometry: Geometry | undefined) => {
  return geometry instanceof MultiLineString;
};

export const isPoint = (geometry: Geometry | undefined) => {
  return geometry instanceof Point;
};

export const isLineGeometry = (geometry: Geometry | undefined) => {
  if (!geometry) return false;
  return geometry.getType() === 'LineString';
};
/**
 * A strict way to check Geometry is Rectangular Box
 * with the help of Claude AI - 2026/01/29
 * @param geometry
 * @returns
 */
export const isRectGeometry = (geometry: Geometry | undefined) => {
  if (!geometry) return false;
  const shape = geometry.getType();
  if (shape != 'Polygon') return false;
  // more strict checking:
  const [vertexes] = (geometry as Polygon).getCoordinates();
  if (vertexes.length != 5) return false; // not 4 vertexs

  const [p0, p1, p2, p3] = vertexes;

  // Helper to calculate dot product of two vectors
  const dotProduct = (v1: Coordinate, v2: Coordinate): number => {
    return v1[0] * v2[0] + v1[1] * v2[1];
  };

  // Helper to calculate vector from point a to point b
  const vector = (a: Coordinate, b: Coordinate): Coordinate => {
    return [b[0] - a[0], b[1] - a[1]];
  };

  // Calculate the four side vectors
  const side1 = vector(p0, p1);
  const side2 = vector(p1, p2);
  const side3 = vector(p2, p3);
  const side4 = vector(p3, p0);

  // For a rectangle, adjacent sides must be perpendicular (dot product = 0)
  // Use epsilon for floating point tolerance
  const epsilon = 1e-10;

  return (
    Math.abs(dotProduct(side1, side2)) < epsilon &&
    Math.abs(dotProduct(side2, side3)) < epsilon &&
    Math.abs(dotProduct(side3, side4)) < epsilon &&
    Math.abs(dotProduct(side4, side1)) < epsilon
  );
};

export const isPointGeometry = (geometry: Geometry | undefined) => {
  if (!geometry) return false;
  return geometry.getType() === 'Point';
};

export const getMapExtent = (map: ol.Map) => {
  return map.getView().calculateExtent(map.getSize());
};

export const getShorterSide = (map: ol.Map) => {
  const extent = getMapExtent(map);
  return Math.min(getWidth(extent), getHeight(extent));
};

export const getLineLength = (line: LineString) => {
  return line.getLength();
};

export const getRectangleLongestSide = (rect: Polygon) => {
  // const [left, bottom, right, top] = rect.getExtent();
  // const width = Math.round(right - left);
  // const height = Math.round(top - bottom);
  const extent = rect.getExtent();
  return Math.max(getWidth(extent), getHeight(extent));
};

export const getLongestSide = (feature: Feature | undefined) => {
  if (!feature) return 0;
  const geometry = feature.getGeometry();
  if (isLineString(geometry)) return getLineLength(geometry as LineString);
  if (isPolygon(geometry)) return getRectangleLongestSide(geometry as Polygon);
  return 0;
};

/**
 * Parametric segment-segment intersection.
 * Returns the intersection Coordinate if the two segments [p1,p2] and [p3,p4]
 * cross each other, or null if they are parallel or don't overlap.
 */
export function segmentIntersect(
  p1: Coordinate,
  p2: Coordinate,
  p3: Coordinate,
  p4: Coordinate,
): Coordinate | null {
  const denom =
    (p1[0] - p2[0]) * (p3[1] - p4[1]) - (p1[1] - p2[1]) * (p3[0] - p4[0]);
  if (Math.abs(denom) < 1e-10) return null; // parallel
  const t =
    ((p1[0] - p3[0]) * (p3[1] - p4[1]) - (p1[1] - p3[1]) * (p3[0] - p4[0])) /
    denom;
  const u =
    -((p1[0] - p2[0]) * (p1[1] - p3[1]) - (p1[1] - p2[1]) * (p1[0] - p3[0])) /
    denom;
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return [p1[0] + t * (p2[0] - p1[0]), p1[1] + t * (p2[1] - p1[1])];
  }
  return null;
}

export const getCentreOfFeatures = (
  features: Feature<SimpleGeometry>[],
): Coordinate | undefined => {
  if (!features.length) return;
  if (features.length === 1)
    return features[0].getGeometry()?.getFirstCoordinate();

  const coordinates = features.map((f) =>
    f.getGeometry()?.getFirstCoordinate(),
  );
  const safeCoords = coordinates.filter((c) => !!c) as Coordinate[];
  const boundary = boundingExtent(safeCoords);
  return getCenter(boundary);
};
